import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Crown, TrendingUp, MapPin, Users, Activity } from 'lucide-react';

const CityLeaderboard = () => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    period: 'weekly',
    metric: 'steps'
  });

  useEffect(() => {
    fetchLeaderboard();
  }, [filters]);

  const fetchLeaderboard = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams(filters);
      const response = await axios.get(`/leaderboard/cities?${params}`);
      setLeaderboard(response.data);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="h-6 w-6 text-yellow-500" />;
    if (rank === 2) return <Crown className="h-6 w-6 text-gray-400" />;
    if (rank === 3) return <Crown className="h-6 w-6 text-amber-600" />;
    return <span className="text-lg font-bold text-gray-600">#{rank}</span>;
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold text-gray-900">City Leaderboard</h2>
        <p className="text-gray-600 mt-2">Compare city performance and rankings</p>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-wrap gap-4 items-center">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Period</label>
            <select
              value={filters.period}
              onChange={(e) => setFilters(prev => ({ ...prev, period: e.target.value }))}
              className="input-field"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Metric</label>
            <select
              value={filters.metric}
              onChange={(e) => setFilters(prev => ({ ...prev, metric: e.target.value }))}
              className="input-field"
            >
              <option value="steps">Steps</option>
              <option value="distance">Distance</option>
              <option value="calories">Calories</option>
            </select>
          </div>
        </div>
      </div>

      {/* Leaderboard */}
      <div className="card">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {leaderboard.map((city, index) => (
              <div key={city.city} className={`flex items-center space-x-4 p-4 rounded-lg ${
                index < 3 ? 'bg-gradient-to-r from-yellow-50 to-yellow-100 border border-yellow-200' : 'bg-gray-50'
              }`}>
                <div className="flex-shrink-0 w-12 flex justify-center">
                  {getRankIcon(index + 1)}
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <MapPin className="h-5 w-5 text-gray-500" />
                    <h3 className="text-lg font-semibold text-gray-900">{city.city}</h3>
                  </div>
                  <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
                    <div className="flex items-center space-x-1">
                      <Users className="h-4 w-4" />
                      <span>{city.active_users} active users</span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-gray-900">
                    {filters.metric === 'steps' && city.total_steps?.toLocaleString()}
                    {filters.metric === 'distance' && `${city.total_distance?.toFixed(1)} km`}
                    {filters.metric === 'calories' && city.total_calories?.toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-500">
                    {city.total_steps?.toLocaleString()} steps • {city.total_distance?.toFixed(1)} km
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CityLeaderboard; 